package fr.frux.feature.premium

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.core.common.error.isExpectedFailure
import fr.frux.core.common.logging.Logger
import fr.frux.shared.domain.model.PremiumStatus
import fr.frux.shared.domain.repository.PremiumRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/**
 * État de l'écran Frux+ (#456). [refreshError] est non bloquant : le dernier [status] connu reste
 * affiché. [purchaseError] est un one-shot porté par l'état, Nav3 détruit les effets locaux.
 */
data class PremiumUiState(
    val status: PremiumStatus = PremiumStatus.Unknown,
    val isRefreshing: Boolean = false,
    val purchaseAvailable: Boolean = false,
    val isPurchasing: Boolean = false,
    val refreshError: AppError? = null,
    val purchaseError: AppError? = null,
) {
    /** Rien à montrer tant que la première lecture n'a pas répondu. */
    val isLoading: Boolean
        get() = status is PremiumStatus.Unknown && isRefreshing

    /** Achat proposé seulement à un compte résolu sans Frux+, jamais pendant un achat en vol. */
    val canPurchase: Boolean
        get() = purchaseAvailable && status is PremiumStatus.Free && !isPurchasing
}

/**
 * Écran Frux+ (#456, ADR 0016). Relit l'état à l'ouverture, puis après chaque achat réussi : le
 * client ne décide jamais seul qu'un droit est actif (ADR 0033).
 */
class PremiumViewModel(
    private val repository: PremiumRepository,
    private val purchaseLauncher: PurchaseLauncher?,
) : ViewModel() {
    private val _state = MutableStateFlow(PremiumUiState(purchaseAvailable = purchaseLauncher != null))
    val state: StateFlow<PremiumUiState> = _state.asStateFlow()

    init {
        viewModelScope.launch {
            repository.status.collect { status -> _state.update { it.copy(status = status) } }
        }
        refresh()
    }

    fun refresh() {
        if (_state.value.isRefreshing) return
        _state.update { it.copy(isRefreshing = true, refreshError = null) }
        viewModelScope.launch { reload() }
    }

    fun purchase() {
        val launcher = purchaseLauncher ?: return
        if (!_state.value.canPurchase) return
        // Synchrone, avant tout `launch` : garde anti double appui.
        _state.update { it.copy(isPurchasing = true, purchaseError = null) }
        viewModelScope.launch {
            launcher.launch().fold(
                onSuccess = {
                    _state.update { it.copy(isPurchasing = false, isRefreshing = true, refreshError = null) }
                    reload()
                },
                onFailure = { failure ->
                    log("Achat Frux+ en échec", failure)
                    _state.update { it.copy(isPurchasing = false, purchaseError = failure.asAppError()) }
                },
            )
        }
    }

    fun consumePurchaseError() = _state.update { it.copy(purchaseError = null) }

    private suspend fun reload() {
        repository.refreshPremiumStatus().fold(
            onSuccess = { _state.update { it.copy(isRefreshing = false) } },
            onFailure = { failure ->
                log("Relecture du statut Frux+ en échec", failure)
                _state.update { it.copy(isRefreshing = false, refreshError = failure.asAppError()) }
            },
        )
    }

    // Échec attendu (réseau, refus store) : sous le niveau erreur, le pont vers Sentry ferait un
    // event de chaque refus métier (#310).
    private fun log(
        message: String,
        failure: Throwable,
    ) {
        if (failure.isExpectedFailure) {
            Logger.i(TAG, "$message (${failure::class.simpleName})")
        } else {
            Logger.e(TAG, message, failure)
        }
    }

    private companion object {
        const val TAG = "PremiumViewModel"
    }
}
